import React, { useMemo, useState } from 'react'
import { Typography } from '@material-ui/core'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import Button from '../Button/Button'
import CMSText from '../Global/CMSText'
import { ORDER_STATE, isSubscription } from '../../utils/constants'
import DownloadInvoiceButton from './DownloadInvoiceButton/DownloadInvoiceButton'
import { getOrderInformations } from '../../utils/orders'
import useOrderStyle from './Order.style'
import RentalOrderList from './RentalOrderList'
import {
  OpenModalCancelOrderList,
  OrderInformations,
} from '../../typings/orders'
import ModalCancelOrder from './ModalCancelOrder'
import { DynamicFormDocumentFlexible } from '@/interfaces/common/prismicDynamicForm'
import SecondHandOrderList from './SecondHandOrderList'
import RentalReturnLabelDownload from './RentalReturnLabelDownload'
import Price from '../Shop/Price'

type Tprops = {
  takeback?: any
  main?: any
  account?: any
  orders?: any
  postProductToReturn?: (data: object) => any
  getRightOfWithdrawalInfo: (id: string) => any
  handleDownloadInvoice?: (orderToken: string) => any
  invoiceDownloadError?: boolean
  prismicAccount?: any
  inStoreText?: any
  isInStore?: boolean | undefined
  showSizes?: boolean
  putUserOrder?: any
  getUserOrders?: any
  savForm?: DynamicFormDocumentFlexible
  onOpenSavForm?: (orderNumber: string) => void
  settings?: any
}

const SecondHandAndRentalOrderList = ({
  takeback,
  orders,
  main,
  postProductToReturn,
  getRightOfWithdrawalInfo,
  handleDownloadInvoice,
  invoiceDownloadError,
  prismicAccount,
  showSizes,
  putUserOrder,
  getUserOrders,
  savForm,
  onOpenSavForm,
  settings,
}: Tprops) => {
  const { t } = useTranslation()
  const classes = useOrderStyle()
  const [openModal, setOpenModal] = useState<OpenModalCancelOrderList>({
    open: false,
    currentOrder: null,
  })

  const handleOpen = (currentOrder: string) => {
    setOpenModal({ open: true, currentOrder: currentOrder })
  }
  const handleClose = () => {
    setOpenModal({ open: false, currentOrder: null })
  }

  async function handleCancel(order) {
    await setOpenModal({ open: false, currentOrder: null })
    const response = await putUserOrder(order.tokenValue)
    if (response.type === 'PUT_USER_ORDER_SUCCESS') {
      await getUserOrders()
    }
  }

  const ordersInformations: OrderInformations[] = useMemo(
    () =>
      orders?.map((order, index) =>
        getOrderInformations(order, index, orders.length)
      ),
    [orders]
  )

  return (
    <div className={classes.takeBackAndRentalOrderList}>
      {ordersInformations?.length > 0 &&
        ordersInformations.map((order, index) => {
          return (
            <div className={`order--${order.state}`} key={index}>
              <div className={classes.orderHeader}>
                {order.state === ORDER_STATE.CANCELLED && (
                  <div
                    className={clsx(
                      classes.circleDefault,
                      classes.circleWarning
                    )}
                  ></div>
                )}
                {order.state === ORDER_STATE.COMING && (
                  <div
                    className={clsx(
                      classes.circleDefault,
                      classes.circlePrimary
                    )}
                  ></div>
                )}
                {order.state === ORDER_STATE.RETURN && (
                  <div
                    className={clsx(
                      classes.circleDefault,
                      classes.circleSecondary
                    )}
                  ></div>
                )}
                {order.state === ORDER_STATE.FINISHED && (
                  <div className={classes.circleDefault}></div>
                )}
                <div className={classes.orderTitle}>
                  <Typography variant={'h4'} color={'textSecondary'}>
                    <CMSText
                      asText
                      data={prismicAccount?.data?.command}
                      defaultText={t('texts:account:command')}
                    />
                    &nbsp;
                    {order.orderNumber}
                  </Typography>

                  <Typography variant={'body1'} component={'div'}>
                    <strong>
                      <Price amount={order.total} />
                    </strong>
                  </Typography>
                </div>
              </div>
              <div
                className={clsx(
                  ['order__item', classes.orderItem],
                  order.state === ORDER_STATE.CANCELLED &&
                    classes.orderItemCancelled,
                  order.state === ORDER_STATE.COMING && classes.orderItemComing,
                  order.state === ORDER_STATE.RETURN && classes.orderItemReturn,
                  order.state === ORDER_STATE.FINISHED && classes.orderItem,
                  order.lastOrder && [
                    'order__item--last',
                    classes.orderItemLast,
                  ]
                )}
              >
                <div>
                  <div className={classes.orderHeader}>
                    <div>
                      {order.state === ORDER_STATE.CANCELLED && (
                        <Typography
                          variant={'body1'}
                          color={'inherit'}
                          className={classes.warning}
                        >
                          {t('texts:account:cancelled')}
                          <b>
                            &nbsp;-&nbsp;
                            {order.cancelledAt
                              ? order.cancelledAt
                              : order.orderStartLeasingDate}
                          </b>
                        </Typography>
                      )}
                      {order.state === ORDER_STATE.COMING && (
                        <Typography variant={'body1'} color={'primary'}>
                          <CMSText
                            asText
                            data={prismicAccount?.data?.date}
                            defaultText={t('texts:account:date')}
                          />
                          &nbsp;
                          <b>{order.checkoutCompletedAt}</b>
                        </Typography>
                      )}
                      {order.state === ORDER_STATE.RETURN &&
                        order?.rentalOrderList.length > 0 && (
                          <Typography variant={'body1'} color={'secondary'}>
                            <CMSText
                              asText
                              data={prismicAccount?.data?.date}
                              defaultText={t('texts:account:date')}
                            />
                            &nbsp;
                            <b>{order.orderEndLeasingDate}</b>
                          </Typography>
                        )}
                      {order.state === ORDER_STATE.FINISHED &&
                        order?.rentalOrderList.length > 0 && (
                          <Typography variant={'body1'} color={'textPrimary'}>
                            <CMSText
                              asText
                              data={prismicAccount?.data?.returned_at}
                              defaultText={t('texts:account:returned_at')}
                            />
                            &nbsp;
                            {order.orderEndLeasingDate}
                          </Typography>
                        )}
                    </div>

                    {order?.isCancellable && (
                      <div className={clsx('order-cancel', classes.cancelArea)}>
                        <Button
                          size="small"
                          disableRipple
                          className={clsx(
                            'order-cancel__btn',
                            classes.cancelBtn
                          )}
                          onClick={() => handleOpen(order.orderNumber)}
                        >
                          <CMSText
                            asText
                            data={prismicAccount?.data?.cancel_order_cta_modal}
                            defaultText={t(
                              'texts:account:cancel_order:cta_modal'
                            )}
                          />
                        </Button>
                      </div>
                    )}
                  </div>
                  <Typography variant={'body2'} component={'div'}>
                    {`${order.shippingAddress.street} ${
                      order.shippingAddress.city
                    } ${
                      order.shippingAddress.postcode === '00000'
                        ? ''
                        : order.shippingAddress.postcode
                    }`}
                  </Typography>
                  {order?.rentalOrderList.length > 0 && (
                    <div className={classes.orderContent}>
                      <div className={classes.OrderTitle}>
                        <Typography color={'textSecondary'} variant={'h5'}>
                          <CMSText
                            data={prismicAccount?.data?.leasing_date_from}
                            defaultText={t('texts:account:leasing_date_from')}
                          />
                          &nbsp;
                          <b>{order.orderStartLeasingDate}</b>
                          &nbsp;
                          <CMSText
                            asText
                            data={prismicAccount?.data?.leasing_date_to}
                            defaultText={t('texts:account:leasing_date_to')}
                          />
                          &nbsp;
                          <b>{order.orderEndLeasingDate}</b>
                        </Typography>
                      </div>
                      <RentalOrderList order={order} showSizes={showSizes} />
                      <RentalReturnLabelDownload
                        orderNumber={order.orderNumber}
                        account={prismicAccount}
                        t={t}
                      />
                    </div>
                  )}
                </div>
                {order?.secondHandOrderList.length > 0 && (
                  <div className={classes.orderContent}>
                    <div className={classes.OrderTitle}>
                      <Typography variant={'h5'} color={'textSecondary'}>
                        <CMSText
                          asText
                          data={takeback?.data?.take_back_list_title}
                          defaultText={t('texts:account:take_back_list_title')}
                        />
                        &nbsp; <b>{order.checkoutCompletedAt}</b>
                      </Typography>
                    </div>
                    <SecondHandOrderList
                      order={order}
                      main={main}
                      takeback={takeback?.data}
                      account={prismicAccount}
                      currentOrderNumber={order.orderNumber}
                      postProductToReturn={postProductToReturn}
                      getRightOfWithdrawalInfo={getRightOfWithdrawalInfo}
                      savForm={savForm}
                      onOpenSavForm={onOpenSavForm}
                      settings={settings}
                    />
                  </div>
                )}

                {handleDownloadInvoice && !!order?.invoice?.id && (
                  <div className={classes.downloadInvoiceButton}>
                    <DownloadInvoiceButton
                      accountRes={prismicAccount}
                      orderToken={order.tokenValue}
                      t={t}
                      handleDownloadInvoice={handleDownloadInvoice}
                      invoiceDownloadError={invoiceDownloadError}
                      isSub={isSubscription}
                    />
                  </div>
                )}
              </div>
              {order?.isCancellable && (
                <ModalCancelOrder
                  order={order}
                  prismicAccountData={prismicAccount?.data}
                  openModal={openModal}
                  handleClose={handleClose}
                  handleCancel={handleCancel}
                />
              )}
            </div>
          )
        })}
    </div>
  )
}

export default SecondHandAndRentalOrderList
